import {
    useState,
    useReducer,
    useEffect,
    ChangeEvent,
    KeyboardEvent,
    WheelEvent,
    useRef,
    useCallback,
    useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    GridActionsCellItem,
    GridColDef,
    GridRowId,
    useGridApiRef,
    GridApi,
    GridRenderCellParams,
} from '@mui/x-data-grid';
import localforage from 'localforage';
import BasePaper from '@/components/ui/BasePaper';
import BaseCard from '@/components/ui/BaseCard';
import BaseTextField from '@/components/form/BaseTextField';
import BaseTitle from '@/components/ui/BaseTitle';
import { Search, Create, Delete, Check, Close } from '@mui/icons-material';
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import IconButton from '@mui/material/IconButton';
import BaseButton from '@/components/ui/BaseButton';
import BaseDialog from '@/components/ui/BaseDialog';
import BaseLoading from '@/components/ui/BaseLoading';
import BaseAutocomplete from '@/components/form/BaseAutocomplete';
import {
    AdminStoresPageDialogState,
    AdminStoresPageDialogAction,
    StoreOverviewResponseWithDetail,
    StoreMemberItem,
} from '@/types/pages/admin/stores';
import { fetchStoreOverviewAPI, fetchStoreDetailAPI, createStoreAPI, updateStoreAPI } from '@/api/admin/stores';
import { getInputMinLengthLimit } from '@/utils/inputUtils';
import { fetchClusterDetailAPI, fetchClusterListAPI, findAllUserAPI, syncUserStoreAPI } from '@/api/admin/rbac';
import { useParams } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { Box, DialogActions } from '@mui/material';
import { Link } from 'react-router-dom';
import { useAppContext } from '@/provider/contexts';

import '@/styles/admin.scss';
import { BaseCluster } from '@/types/pages/admin/cluster';
import BaseDataTable from '@/components/ui/BaseDataTable';

import { useTranslation } from 'react-i18next';
import BaseTableColumn from '@/components/ui/BaseTableColumn';

export type apiRefType = React.MutableRefObject<GridApi>;
type MemberOption = StoreMemberItem & { selectAction?: 'all' | 'none' };

function reducer(state: AdminStoresPageDialogState, action: AdminStoresPageDialogAction) {
    switch (action.type) {
        case 'SET_TABLE_LOADING':
            return { ...state, tableLoading: action.payload };
        case 'SET_PAGINATION_MODEL':
            return { ...state, paginationModel: action.payload };
        case 'SET_STORE_TABLE':
            return {
                ...state,
                tableLoading: false,
                store: action.payload,
                store_direct_users: action.payload.store_direct_users || [],
                store_cluster_users: action.payload.store_cluster_users || [],
                rowCount:
                    (action.payload.store_direct_users?.length ?? 0) +
                    (action.payload.store_cluster_users?.length ?? 0),
            };
        case 'SET_IS_CLUSTER_LIST_LOADING':
            return { ...state, isClusterListLoading: action.payload };
        case 'SET_CLUSTER_LIST':
            return { ...state, isClusterListLoading: false, clusterList: action.payload };
        case 'SET_MEMBER_LIST':
            return {
                ...state,
                store: {
                    ...state.store,
                    store_member_list: action.payload,
                },
            };
        default:
            return state;
    }
}

function AdminStorePageDialog() {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [searchError, setSearchError] = useState('');
    const [sFormDetail, setSFormDetail] = useState({
        store_name: '',
        store_code: '',
        distributor_name: '',
        zip_code: '',
        store_cluster: [] as BaseCluster[],
        store_address: '',
    });
    const [storeNameError, setStoreNameError] = useState(false);
    const [storeCodeError, setStoreCodeError] = useState('');
    const [isMemberDialogOpen, setIsMemberDialogOpen] = useState(false);
    const [allUserList, setAllUserList] = useState<StoreMemberItem[]>([]);
    const [, setStoreMemberSet] = useState(new Set());
    const [newMemberList, setNewMemberList] = useState<StoreMemberItem[]>([]);

    const { setNotification } = useAppContext();
    const { id } = useParams();
    const displayTitle = id ? t('Edit') : t('New');

    const [directLoading, setDirectLoading] = useState(true);
    const [clusterLoading, setClusterLoading] = useState(true);

    const defaultState: AdminStoresPageDialogState = {
        tableLoading: false,
        store: {} as StoreOverviewResponseWithDetail,
        store_direct_users: [],
        store_cluster_users: [],
        rowCount: 0,
        paginationModel: {
            page: 0,
            pageSize: 5,
        },
        isClusterListLoading: false,
        clusterList: [] as BaseCluster[],
    };

    const [{ tableLoading, store, isClusterListLoading, clusterList }, dispatch] = useReducer(reducer, defaultState);
    const fetchAbortControllerRef = useRef<AbortController | null>(null);
    const latestFetchIdRef = useRef(0);

    // const defaultPageParams = {
    //     current_page: paginationModel.page + 1,
    //     data_per_page: paginationModel.pageSize,
    // };

    // const getRowId = (row: StoreMemberItem) => {
    //     return row.user_id;
    // };

    // const columns: GridColDef[] = [
    //     {
    //         field: 'username',
    //         headerName: 'Member Name',
    //         flex: 1,
    //         minWidth: 140,
    //     },
    //     { field: 'email', headerName: 'Email', flex: 1, minWidth: 90 },
    //     {
    //         field: 'user_type',
    //         headerName: 'Type',
    //         flex: 0.5,
    //         minWidth: 100,
    //         renderCell: ({ row }) => {
    //             if ((row as any).isNew) return <em>New</em>;
    //             return row.user_type === 'direct' ? 'Direct' : 'Cluster';
    //         },
    //     },
    //     {
    //         field: 'action',
    //         type: 'actions',
    //         headerName: 'Action',
    //         getActions: ({ id, row }) => {
    //             if ((row as any).isNew) return [];
    //             const isDirect = row.user_type === 'direct';
    //             return [
    //                 <GridActionsCellItem
    //                     icon={
    //                         <Tooltip title={isDirect ? 'Delete user' : 'Cannot remove cluster user'} arrow>
    //                             <span>
    //                                 <Delete />
    //                             </span>
    //                         </Tooltip>
    //                     }
    //                     label="Delete"
    //                     disabled={!isDirect}
    //                     onClick={() => isDirect && deleteItem(id)}
    //                 />,
    //             ];
    //         },
    //     },
    // ];

    const deleteItem = useCallback(
        (id: GridRowId) => {
            const updatedList = (store.store_member_list ?? []).filter((m) => m.user_id !== id);
            dispatch({ type: 'SET_MEMBER_LIST', payload: updatedList });
            setStoreMemberSet(new Set(updatedList.map((m) => m.user_id)));
        },
        [store.store_member_list],
    );

    const renderSimpleCell = useCallback((params: GridRenderCellParams) => {
        const value = params.value;
        const displayValue = value === null || value === undefined || value === '' ? '-' : String(value);
        return <BaseTableColumn columnValue={displayValue} />;
    }, []);

    const directCols: GridColDef[] = useMemo(
        () => [
            { field: 'username', headerName: t('Member Name'), flex: 1, renderCell: renderSimpleCell },
            { field: 'email', headerName: t('Email'), flex: 1, renderCell: renderSimpleCell },
            {
                field: 'action',
                type: 'actions',
                headerName: t('Action'),
                getActions: ({ id }) => [
                    <GridActionsCellItem icon={<Delete />} label={t('Delete')} onClick={() => deleteItem(id)} />,
                ],
            },
        ],
        [deleteItem, renderSimpleCell, t],
    );

    const clusterCols: GridColDef[] = useMemo(
        () => [
            { field: 'username', headerName: t('Member Name'), flex: 1, renderCell: renderSimpleCell },
            { field: 'email', headerName: t('Email'), flex: 1, renderCell: renderSimpleCell },
        ],
        [renderSimpleCell, t],
    );

    const fetchStoreDetailHandler = async (storeId?: number) => {
        fetchAbortControllerRef.current?.abort();
        const fetchId = latestFetchIdRef.current + 1;
        latestFetchIdRef.current = fetchId;
        const abortController = new AbortController();
        fetchAbortControllerRef.current = abortController;
        setDirectLoading(true);
        setClusterLoading(true);
        dispatch({ type: 'SET_TABLE_LOADING', payload: true });
        const corporationId = (await localforage.getItem('corporation_id')) as number;
        const idToFetch = storeId ?? (id ? Number(id) : undefined);
        if (!idToFetch) {
            setDirectLoading(false);
            dispatch({ type: 'SET_TABLE_LOADING', payload: false });
            return;
        }

        let data = {} as StoreOverviewResponseWithDetail;
        try {
            const ov = await fetchStoreOverviewAPI(idToFetch, { signal: abortController.signal });
            if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;
            data = ov.data.data as StoreOverviewResponseWithDetail;
            setStoreMemberSet(new Set((data.store_member_list ?? []).map((m) => m.user_id)));

            const dt = await fetchStoreDetailAPI(idToFetch, { signal: abortController.signal });
            if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;
            data.detail = dt.data.data;
        } catch (err) {
            if (abortController.signal.aborted) return;
            console.error(err);
            setNotification({ message: t('An unexpected error occurred'), severity: 'error' });
        }
        const rs3 = await findAllUserAPI(
            {
                corporate_id: corporationId,
                keyword: '#ALL#',
            },
            { signal: abortController.signal },
        );
        if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;
        setAllUserList(
            rs3.data.data.map((u) => ({
                user_id: u.user_id,
                username: u.username,
                email: u.email,
                user_level: u.user_level,
                user_status: u.user_status,
                user_type: 'direct' as const,
            })),
        );
        dispatch({
            type: 'SET_STORE_TABLE',
            payload: {
                ...data,
                store_direct_users: data.store_direct_users,
                store_cluster_users: data.store_cluster_users,
            },
        });
        setDirectLoading(false);

        const clusters = clusterList.filter((clusterItem: BaseCluster) => {
            return data.detail?.store_info?.clusters.find((cluster) => cluster.id === clusterItem.id);
        });

        setSFormDetail({
            store_name: data.detail?.store_info?.store_name ?? '',
            store_code: data.detail?.store_info?.store_code ?? '',
            distributor_name: data.detail?.store_info?.distributor_name ?? '',
            zip_code: data.detail?.store_info?.zip_code ?? '',
            store_cluster: clusters ?? ([] as BaseCluster[]),
            store_address: data.detail?.store_info?.store_address ?? '',
        });

        if (latestFetchIdRef.current === fetchId) {
            fetchAbortControllerRef.current = null;
        }
    };

    const [clusterPreviewMembers, setClusterPreviewMembers] = useState<StoreMemberItem[]>([]);
    useEffect(() => {
        let isMounted = true;
        const fetchId = latestFetchIdRef.current + 1;
        latestFetchIdRef.current = fetchId;
        fetchAbortControllerRef.current?.abort();
        const abortController = new AbortController();
        fetchAbortControllerRef.current = abortController;

        (async () => {
            const stopLoading = () => {
                dispatch({ type: 'SET_TABLE_LOADING', payload: false });
                setDirectLoading(false);
                setClusterLoading(false);
                fetchAbortControllerRef.current = null;
            };

            try {
                dispatch({ type: 'SET_TABLE_LOADING', payload: true });
                setDirectLoading(true);
                setClusterLoading(true);

                const corporationId = await localforage.getItem<number>('corporation_id');
                const storeId = id ? Number(id) : undefined;
                const [clusterListRes, overviewRes, detailRes, usersRes] = await Promise.all([
                    fetchClusterListAPI({
                        cid: corporationId!,
                        f_cluster_name: '',
                        current_page: null,
                        data_per_page: null,
                    }),
                    storeId
                        ? fetchStoreOverviewAPI(storeId, { signal: abortController.signal })
                        : Promise.resolve(null),
                    storeId ? fetchStoreDetailAPI(storeId, { signal: abortController.signal }) : Promise.resolve(null),
                    findAllUserAPI(
                        { corporate_id: corporationId!, keyword: '#ALL#' },
                        { signal: abortController.signal },
                    ),
                ]);
                if (!isMounted || abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;

                const hasPreloadFailure =
                    clusterListRes.data.isSuccess === false ||
                    usersRes.data.isSuccess === false ||
                    overviewRes?.data.isSuccess === false ||
                    detailRes?.data.isSuccess === false;

                if (hasPreloadFailure) {
                    setNotification({ message: t('An unexpected error occurred'), severity: 'error' });
                    stopLoading();
                    return;
                }

                setAllUserList(
                    (usersRes.data.data ?? []).map((u) => ({
                        user_id: u.user_id,
                        username: u.username,
                        email: u.email,
                        user_level: u.user_level,
                        user_status: u.user_status,
                        user_type: 'direct' as const,
                    })),
                );

                if (overviewRes) {
                    const direct = (overviewRes.data.data as StoreOverviewResponseWithDetail).store_direct_users ?? [];
                    setStoreMemberSet(new Set(direct.map((m) => m.user_id)));
                }

                if (clusterListRes.data.isSuccess !== false) {
                    dispatch({
                        type: 'SET_CLUSTER_LIST',
                        payload: clusterListRes.data.data.aaData,
                    });
                }

                // 5) Direct members overview → table
                if (overviewRes) {
                    const overview = overviewRes.data.data as StoreOverviewResponseWithDetail;
                    dispatch({
                        type: 'SET_STORE_TABLE',
                        payload: {
                            ...overview,
                            store_direct_users: overview.store_direct_users ?? [],
                            store_cluster_users: overview.store_cluster_users ?? [],
                        },
                    });
                }

                if (detailRes) {
                    const detail = detailRes.data.data;
                    const storeInfo = detail?.store_info;
                    if (!storeInfo) {
                        throw new Error('Missing store detail');
                    }

                    const pickedClusters = clusterListRes.data.data.aaData.filter((c) =>
                        (storeInfo.clusters ?? []).some((sc: { id: number }) => sc.id === c.id),
                    );

                    setSFormDetail({
                        store_name: storeInfo.store_name,
                        store_code: storeInfo.store_code,
                        distributor_name: storeInfo.distributor_name,
                        zip_code: storeInfo.zip_code,
                        store_address: storeInfo.store_address,
                        store_cluster: pickedClusters,
                    });

                    const responses = await Promise.all(
                        pickedClusters.map((c) => fetchClusterDetailAPI(c.id, { cid: corporationId! })),
                    );
                    const members = responses
                        .flatMap((r) => r.data.data.users)
                        .map((u) => ({
                            user_id: u.id,
                            username: u.username,
                            email: u.email,
                            user_type: 'cluster' as const,
                            user_level: 1,
                            user_status: 1,
                        }))
                        .filter((m, i, arr) => arr.findIndex((x) => x.user_id === m.user_id) === i);

                    if (!isMounted || abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;
                    setClusterPreviewMembers(members);
                }

                if (latestFetchIdRef.current === fetchId) {
                    stopLoading();
                }
            } catch (error) {
                if (!isMounted || abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;
                console.error(error);
                setNotification({ message: t('An unexpected error occurred'), severity: 'error' });
                stopLoading();
            }
        })();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [id]);

    const directApiRef = useGridApiRef<GridApi>();
    const clusterApiRef = useGridApiRef<GridApi>();

    const handleInputStoreDetail = (f: string, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        if (f === 'store_name') {
            setStoreNameError(value.length ? false : true);
        } else if (f === 'store_code') {
            setStoreCodeError('');
        }
        setSFormDetail((prevState) => ({
            ...prevState,
            [f]: value,
        }));
    };
    const clusterReqId = useRef(0);
    const handleCluster = async (_: unknown, value: BaseCluster[]) => {
        clusterReqId.current += 1;
        const thisReq = clusterReqId.current;
        setSFormDetail((prev) => ({ ...prev, store_cluster: value }));

        if (value.length === 0) {
            setClusterPreviewMembers([]);
            setClusterLoading(false);
            return;
        }

        setClusterLoading(true);

        const corporationId = await localforage.getItem<number>('corporation_id');
        const responses = await Promise.all(
            value.map((cluster) => fetchClusterDetailAPI(cluster.id, { cid: corporationId! })),
        );

        if (thisReq !== clusterReqId.current) {
            return;
        }
        const members = responses
            .flatMap((r) => r.data.data.users)
            .map((u) => ({
                user_id: u.id,
                username: u.username,
                email: u.email,
                user_type: 'cluster' as const,
                user_level: 1,
                user_status: 1,
            }))
            .filter((m, i, arr) => arr.findIndex((x) => x.user_id === m.user_id) === i);

        setClusterPreviewMembers(members);
        setClusterLoading(false);
    };

    const handleInputSearch = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearch(e.target.value);
    };

    const enterKeyHandler = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleClickSearch();
        }
    };

    const handleConfirmAddMember = () => {
        const flaggedNew = newMemberList.map((u) => ({
            ...u,
            isNew: true as const,
            user_type: 'direct' as const,
        }));

        const existingDirectIds = new Set(
            (store.store_member_list ?? []).filter((m) => m.user_type === 'direct').map((m) => m.user_id),
        );

        const toAdd = flaggedNew.filter((n) => !existingDirectIds.has(n.user_id));

        const updatedList = [...(store.store_member_list ?? []), ...toAdd];

        dispatch({ type: 'SET_MEMBER_LIST', payload: updatedList });
        setStoreMemberSet(new Set(updatedList.map((m) => m.user_id)));
        setNewMemberList([]);
        setIsMemberDialogOpen(false);
    };

    const handleChangeAddMember = (v: StoreMemberItem[]) => {
        setNewMemberList(v);
    };

    const handleClickSearch = () => {
        const minLength = getInputMinLengthLimit(search);

        if (search.trim().length !== 0 && search.length < minLength) {
            setSearchError(t('Keyword length validation', { count: minLength }));
            return;
        }
        setSearchError('');

        const filter = [search];

        directApiRef.current?.setQuickFilterValues(filter);
        clusterApiRef.current?.setQuickFilterValues(filter);
    };

    const handleSave = async () => {
        setStoreNameError(false);
        setStoreCodeError('');

        const name = sFormDetail.store_name.trim();
        const code = sFormDetail.store_code.trim();
        let hasError = false;

        if (!name) {
            setStoreNameError(true);
            hasError = true;
        }
        if (!code) {
            setStoreCodeError('Required');
            hasError = true;
        } else if (!/^[a-zA-Z0-9]+$/.test(sFormDetail.store_code)) {
            setStoreCodeError('Letters and numbers only.');
            hasError = true;
        }

        if (hasError) {
            if (!name || !code) {
                setNotification({
                    message: t('Please fill in all required fields.'),
                    severity: 'error',
                });
            }
            return;
        }
        let syncStoreId: number | null = null;
        const actionText = id ? t('Edit') : t('Create');

        const corporationId = (await localforage.getItem('corporation_id')) as number;

        const clusterIds = sFormDetail.store_cluster.map((cluster) => cluster.id);

        if (!sFormDetail.store_name.length) {
            setStoreNameError(true);
        } else if (!sFormDetail.store_code.length) {
            setStoreCodeError('Required');
        } else {
            if (id) {
                try {
                    const detailData = {
                        ...sFormDetail,
                        store_corporation_id: corporationId,
                        store_cluster: clusterIds,
                    };
                    const {
                        data: { isSuccess, message },
                    } = await updateStoreAPI(id, detailData);
                    if (isSuccess) {
                        syncStoreId = Number(id);

                        setNotification({
                            message: t('Update successful!'),
                            severity: 'success',
                        });
                    } else {
                        throw new Error(message);
                    }
                } catch (error: unknown) {
                    if (error instanceof Error) {
                        setNotification({
                            message: error?.message || t('An unexpected error occurred'),
                            severity: 'error',
                        });
                        return;
                    }
                    console.error('An unexpected error occurred:', error);
                    return;
                }
            } else {
                try {
                    const {
                        data: { data, isSuccess, message },
                    } = await createStoreAPI({
                        ...sFormDetail,
                        store_corporation_id: corporationId,
                        store_cluster: clusterIds,
                    });
                    if (isSuccess) {
                        syncStoreId = data.id;
                        setNotification({
                            message: t('Create successful!'),
                            severity: 'success',
                        });
                    } else {
                        throw new Error(message);
                    }
                } catch (error: unknown) {
                    if (error instanceof Error) {
                        setNotification({
                            message: error?.message || t('An unexpected error occurred'),
                            severity: 'error',
                        });
                        return;
                    }
                    console.error('An unexpected error occurred:', error);
                    return;
                }
            }
        }

        if (syncStoreId) {
            const directIds = directMembers.map((m) => m.user_id);
            try {
                const rs = await syncUserStoreAPI({
                    store_id: syncStoreId,
                    uid: directIds,
                });
                if (rs.data?.status == 200) {
                    await fetchStoreDetailHandler(syncStoreId);
                    setNotification({
                        message: `${actionText} successful!`,
                        severity: 'success',
                    });
                } else {
                    setNotification({
                        message: t('Create failed!'),
                        severity: 'error',
                    });
                }
                syncStoreId = null;
            } catch (error) {
                console.error('An unexpected error occurred:', error);
                setNotification({
                    message: t('An unexpected error occurred'),
                    severity: 'error',
                });
            }
        } else {
            setNotification({
                message: `${actionText} ${t('successful!')}`,
                severity: 'success',
            });
        }
        navigate('/admin/store');

        return false;
    };

    const excludedIds = new Set((store.store_member_list ?? []).map((m) => m.user_id));
    const filteredOptions = allUserList.filter((user) => !excludedIds.has(user.user_id));
    const memberOptions: MemberOption[] = [
        {
            user_id: -1,
            username: t('Select All'),
            email: '',
            user_type: '',
            user_level: 0,
            user_status: 0,
            selectAction: 'all',
        },
        {
            user_id: -2,
            username: t('Unselect All'),
            email: '',
            user_type: '',
            user_level: 0,
            user_status: 0,
            selectAction: 'none',
        },
        ...filteredOptions,
    ];
    const allMembers = store.store_member_list ?? [];
    const directMembers = allMembers.filter((m) => m.user_type === 'direct' || (m as any).isNew);

    return (
        <>
            <BasePaper className={'admin-paper'}>
                <BaseTitle icon={<DomainAddIcon />} title={`${displayTitle} ${t('Store')}`} />

                <BaseCard>
                    <BaseLoading open={tableLoading} />

                    <div className="card-textfield-wrapper">
                        <BaseTextField
                            id="store_code"
                            label={t('Store Code')}
                            required
                            placeholder={t('Store Code')}
                            error={Boolean(storeCodeError)}
                            helperText={storeCodeError ? t(storeCodeError) : ''}
                            sx={{ my: 0, width: '100%' }}
                            value={sFormDetail.store_code}
                            onChange={(e) => handleInputStoreDetail('store_code', e)}
                        />
                        <BaseTextField
                            id="store_name"
                            label={t('Store Name')}
                            required
                            error={storeNameError}
                            helperText={storeNameError ? t('Required') : ''}
                            placeholder={t('Store Name')}
                            sx={{ my: 0, width: '100%' }}
                            value={sFormDetail.store_name}
                            onChange={(e) => handleInputStoreDetail('store_name', e)}
                        />
                        <BaseTextField
                            id="zip_code"
                            label={t('Zip code')}
                            placeholder={t('Zip code')}
                            sx={{ my: 0, width: '100%' }}
                            value={sFormDetail.zip_code}
                            onChange={(e) => handleInputStoreDetail('zip_code', e)}
                        />
                    </div>
                    <div className="card-textfield-wrapper">
                        <BaseAutocomplete<BaseCluster, true>
                            multiple
                            label={t('Cluster')}
                            id="store_cluster"
                            loading={isClusterListLoading}
                            options={clusterList}
                            getOptionLabel={(opt) => opt.cluster_name}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            value={sFormDetail.store_cluster ?? []}
                            onChange={handleCluster}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const { key, ...tagProps } = getTagProps({
                                        index,
                                    });
                                    return (
                                        <Chip variant="outlined" label={option.cluster_name} key={key} {...tagProps} />
                                    );
                                })
                            }
                            sx={{ my: 2, width: '100%' }}
                        />
                        <BaseTextField
                            id="store_address"
                            label={t('Store Address')}
                            placeholder={t('Store Address')}
                            sx={{ my: 2, width: '100%' }}
                            value={sFormDetail.store_address}
                            onChange={(e) => handleInputStoreDetail('store_address', e)}
                        />
                        <Box sx={{ my: 2, width: '100%' }}></Box>
                    </div>
                </BaseCard>

                <BaseCard>
                    <div className="card-header">
                        <BaseTextField
                            placeholder={t('Search Member...')}
                            sx={{ my: 2, width: 300 }}
                            onChange={(e) => handleInputSearch(e)}
                            onKeyUp={enterKeyHandler}
                            error={!!searchError}
                            helperText={searchError}
                            endIcon={
                                <IconButton type="button" aria-label={t('Search')} onClick={() => handleClickSearch()}>
                                    <Search />
                                </IconButton>
                            }
                        ></BaseTextField>
                        <BaseButton
                            startIcon={<Create />}
                            onClick={() => {
                                setNewMemberList([]);
                                setIsMemberDialogOpen(true);
                            }}
                        >
                            {t('New Member')}
                        </BaseButton>
                        <BaseDialog
                            dialogTitle={t('New Member')}
                            confirmButtonText={t('CONFIRM')}
                            cancelButtonText={t('CANCEL')}
                            onConfirm={handleConfirmAddMember}
                            onClose={() => setIsMemberDialogOpen(false)}
                            open={isMemberDialogOpen}
                            disableScrollLock={false}
                        >
                            <Autocomplete<MemberOption, true>
                                multiple
                                filterSelectedOptions
                                id="select-member"
                                sx={{ width: 'min(400px, 100%)' }}
                                value={newMemberList}
                                options={memberOptions}
                                getOptionLabel={(option) => option.username || ''}
                                renderOption={(props, option) => (
                                    <Box
                                        component="li"
                                        {...props}
                                        sx={
                                            option.selectAction
                                                ? {
                                                      bgcolor: 'action.hover',
                                                      color: 'primary.main',
                                                      fontWeight: 'bold',
                                                      borderBottom:
                                                          option.selectAction === 'none' ? 1 : undefined,
                                                      borderColor: 'divider',
                                                  }
                                                : undefined
                                        }
                                    >
                                        {option.selectAction === 'all' && <Check sx={{ mr: 1 }} />}
                                        {option.selectAction === 'none' && <Close sx={{ mr: 1 }} />}
                                        {option.username}
                                    </Box>
                                )}
                                getOptionDisabled={(option) =>
                                    (option.selectAction === 'all' &&
                                        (filteredOptions.length === 0 ||
                                            newMemberList.length === filteredOptions.length)) ||
                                    (option.selectAction === 'none' && newMemberList.length === 0)
                                }
                                onChange={(_, v) => {
                                    const action = v.find((option) => option.selectAction)?.selectAction;
                                    if (action === 'all') setNewMemberList(filteredOptions);
                                    else if (action === 'none') setNewMemberList([]);
                                    else handleChangeAddMember(v);
                                }}
                                slotProps={{
                                    paper: {
                                        onWheel: (event: WheelEvent<HTMLDivElement>) => {
                                            const listbox =
                                                event.currentTarget.querySelector<HTMLElement>('[role="listbox"]');
                                            const reachedEdge =
                                                !listbox ||
                                                (event.deltaY < 0 && listbox.scrollTop === 0) ||
                                                (event.deltaY > 0 &&
                                                    Math.ceil(listbox.scrollTop + listbox.clientHeight) >=
                                                        listbox.scrollHeight);

                                            if (reachedEdge) event.preventDefault();
                                            event.stopPropagation();
                                        },
                                    },
                                    listbox: {
                                        sx: {
                                            maxHeight: 'min(40vh, 320px)',
                                            overflowY: 'auto',
                                            overscrollBehavior: 'contain',
                                        },
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        variant="standard"
                                        label={t('New Member')}
                                        placeholder={t('Member')}
                                        sx={{ my: 2 }}
                                    />
                                )}
                            />
                        </BaseDialog>
                    </div>

                    {/* <BaseDataTable
                        rows={store.store_member_list ?? []}
                        columns={columns}
                        loading={tableLoading}
                        getRowId={getRowId}
                        apiRef={apiRef}
                        slotProps={{
                            loadingOverlay: {
                                variant: 'linear-progress',
                                noRowsVariant: 'linear-progress',
                            },
                        }}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 5,
                                },
                            },
                        }}
                    /> */}

                    <BaseCard>
                        <Box component="h3" sx={{ m: 2 }}>
                            {t('Direct Members')}
                        </Box>
                        <BaseDataTable
                            rows={directMembers}
                            columns={directCols}
                            loading={directLoading}
                            getRowId={(row) => row.user_id}
                            apiRef={directApiRef}
                            slotProps={{
                                loadingOverlay: {
                                    variant: 'linear-progress',
                                    noRowsVariant: 'linear-progress',
                                },
                            }}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 5 } },
                            }}
                        />
                    </BaseCard>

                    <BaseCard>
                        <Box component="h3" sx={{ m: 2 }}>
                            {t('Cluster Members')}
                        </Box>
                        <BaseDataTable
                            rows={clusterPreviewMembers}
                            columns={clusterCols}
                            loading={clusterLoading}
                            getRowId={(row) => row.user_id}
                            apiRef={clusterApiRef}
                            slotProps={{
                                loadingOverlay: {
                                    variant: 'linear-progress',
                                    noRowsVariant: 'linear-progress',
                                },
                            }}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 5 } },
                            }}
                        />
                    </BaseCard>

                    <DialogActions>
                        <BaseButton
                            variant="contained"
                            startIcon={<Check />}
                            style={{
                                backgroundColor: 'var(--confirm-btn-color)',
                                color: '#fff',
                                fontWeight: 'bold',
                            }}
                            onClick={() => handleSave()}
                        >
                            {t('Save')}
                        </BaseButton>
                        <Link to="/admin/store">
                            <BaseButton
                                variant="outlined"
                                startIcon={<Close />}
                                style={{
                                    borderColor: 'var(--cancel-btn-color)',
                                    color: 'var(--cancel-btn-color)',
                                    fontWeight: 'bold',
                                }}
                            >
                                {t('Cancel')}
                            </BaseButton>
                        </Link>
                    </DialogActions>
                </BaseCard>
            </BasePaper>
        </>
    );
}

export default AdminStorePageDialog;
